// ===========================================
// Contact Information Page
// ===========================================
// Allows parents to view and edit their contact information
// including primary contact, secondary contact, and emergency contact

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Skeleton,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonOutline as PersonOutlineIcon,
  LocalHospital as EmergencyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ExpiredIcon,
  Info as InfoIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../contexts/useAuth';
import { useParentProfile, useUpdateParentProfile } from '../../hooks/useUsers';
import type { UpdateParentProfileData } from '../../api/users.api';
import {
  useContact2InvitationStatus,
  useSendContact2Invitation,
  useResendContact2Invitation,
  useCancelContact2Invitation,
} from '../../hooks/useInvitations';
import { useMyChildren, useUpdateStudentNotes, useUpdateChildBirthDate } from '../../hooks/useParentProfile';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { formatUTCDate, schoolTodayUtcMidnight } from '../../utils/dateUtils';
import { isPlaceholderBirthDate } from '../../utils/birthdays';

// ===========================================
// TYPES
// ===========================================

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: string;
}

interface FormData {
  familyName: string;
  contact1: ContactFormData;
  contact2: ContactFormData;
  emergencyContact: Omit<ContactFormData, 'email'>;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

const getInitialFormData = (): FormData => ({
  familyName: '',
  contact1: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: 'Parent',
  },
  contact2: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: '',
  },
  emergencyContact: {
    firstName: '',
    lastName: '',
    phone: '',
    relationship: '',
  },
});

/** The stored `@db.Date` as `YYYY-MM-DD`, or '' when there is none. */
const birthDateKey = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : iso.slice(0, 10);
};

/**
 * 1 January is the school's "date unknown" marker AND a real birthday, and the two
 * cannot be told apart (Andrew, 2026-10-14). Show the stored date and ask the
 * parent to check it, rather than claiming it is missing.
 */
const isFirstOfJanuary = (key: string): boolean => key !== '' && isPlaceholderBirthDate(new Date(key));

/** The server's message for a refused save, or a generic one. */
const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

// ===========================================
// COMPONENT
// ===========================================

export default function ContactInformationPage() {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { user } = useAuth();

  // Queries and mutations
  const { data: profile, isLoading, error } = useParentProfile();
  const updateProfile = useUpdateParentProfile();
  const { data: children } = useMyChildren();
  const updateStudentNotes = useUpdateStudentNotes();

  // Per-student date of birth editing state: map of studentId -> draft `YYYY-MM-DD`
  const updateChildBirthDate = useUpdateChildBirthDate();
  const [dobEditing, setDobEditing] = useState<Record<string, boolean>>({});
  const [dobDraft, setDobDraft] = useState<Record<string, string>>({});
  const timeZone = useSchoolTimezone();
  const todayKey = schoolTodayUtcMidnight(timeZone).toISOString().slice(0, 10);

  // Per-student notes editing state: map of studentId -> draft notes
  const [notesEditing, setNotesEditing] = useState<Record<string, boolean>>({});
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  // Invitation hooks
  const { data: invitationStatus, isLoading: isLoadingInvitation } = useContact2InvitationStatus(
    profile?.id || null
  );
  const sendInvitation = useSendContact2Invitation();
  const resendInvitation = useResendContact2Invitation();
  const cancelInvitation = useCancelContact2Invitation();

  // Populate form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        familyName: profile.familyName || '',
        contact1: {
          firstName: profile.contact1.firstName || '',
          lastName: profile.contact1.lastName || '',
          email: profile.contact1.email || '',
          phone: profile.contact1.phone || '',
          relationship: profile.contact1.relationship || 'Parent',
        },
        contact2: {
          firstName: profile.contact2?.firstName || '',
          lastName: profile.contact2?.lastName || '',
          email: profile.contact2?.email || '',
          phone: profile.contact2?.phone || '',
          relationship: profile.contact2?.relationship || '',
        },
        emergencyContact: {
          firstName: profile.emergencyContact.firstName || '',
          lastName: profile.emergencyContact.lastName || '',
          phone: profile.emergencyContact.phone || '',
          relationship: profile.emergencyContact.relationship || '',
        },
      });
    }
  }, [profile]);

  // Handle form field changes
  const handleChange = (
    section: 'contact1' | 'contact2' | 'emergencyContact',
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Handle cancel - reset form to original values
  const handleCancel = () => {
    if (profile) {
      setFormData({
        familyName: profile.familyName || '',
        contact1: {
          firstName: profile.contact1.firstName || '',
          lastName: profile.contact1.lastName || '',
          email: profile.contact1.email || '',
          phone: profile.contact1.phone || '',
          relationship: profile.contact1.relationship || 'Parent',
        },
        contact2: {
          firstName: profile.contact2?.firstName || '',
          lastName: profile.contact2?.lastName || '',
          email: profile.contact2?.email || '',
          phone: profile.contact2?.phone || '',
          relationship: profile.contact2?.relationship || '',
        },
        emergencyContact: {
          firstName: profile.emergencyContact.firstName || '',
          lastName: profile.emergencyContact.lastName || '',
          phone: profile.emergencyContact.phone || '',
          relationship: profile.emergencyContact.relationship || '',
        },
      });
    }
    setIsEditing(false);
  };

  // Handle save
  const handleSave = async () => {
    const updateData: UpdateParentProfileData = {
      // familyName intentionally omitted — only admins can rename a family.
      // Contact 1
      contact1FirstName: formData.contact1.firstName,
      contact1LastName: formData.contact1.lastName,
      contact1Email: formData.contact1.email,
      contact1Phone: formData.contact1.phone,
      contact1Relationship: formData.contact1.relationship,
      // Contact 2 (convert empty strings to null)
      contact2FirstName: formData.contact2.firstName || null,
      contact2LastName: formData.contact2.lastName || null,
      contact2Email: formData.contact2.email || null,
      contact2Phone: formData.contact2.phone || null,
      contact2Relationship: formData.contact2.relationship || null,
      // Emergency Contact
      emergencyFirstName: formData.emergencyContact.firstName,
      emergencyLastName: formData.emergencyContact.lastName,
      emergencyPhone: formData.emergencyContact.phone,
      emergencyRelationship: formData.emergencyContact.relationship,
    };

    try {
      await updateProfile.mutateAsync(updateData);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Contact information updated successfully',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to update contact information. Please try again.',
        severity: 'error',
      });
    }
  };

  // Handle sending invitation
  const handleSendInvitation = async () => {
    if (!profile?.id) return;
    try {
      await sendInvitation.mutateAsync(profile.id);
      setSnackbar({
        open: true,
        message: 'Invitation sent successfully! Check the secondary contact\'s email.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send invitation. Please try again.',
        severity: 'error',
      });
    }
  };

  // Handle resending invitation
  const handleResendInvitation = async () => {
    if (!profile?.id) return;
    try {
      await resendInvitation.mutateAsync(profile.id);
      setSnackbar({
        open: true,
        message: 'Invitation resent successfully!',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to resend invitation. Please try again.',
        severity: 'error',
      });
    }
  };

  // Handle cancelling invitation
  const handleCancelInvitation = async () => {
    if (!profile?.id) return;
    try {
      await cancelInvitation.mutateAsync(profile.id);
      setCancelDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Invitation cancelled.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to cancel invitation. Please try again.',
        severity: 'error',
      });
    }
  };

  // Get invitation status display info
  const getInvitationStatusInfo = () => {
    if (!invitationStatus) return null;

    if (invitationStatus.status === 'ALREADY_REGISTERED') {
      return {
        label: 'Has Account',
        color: 'success' as const,
        icon: <CheckCircleIcon fontSize="small" />,
      };
    }

    switch (invitationStatus.status) {
      case 'PENDING':
        return {
          label: 'Invitation Pending',
          color: 'warning' as const,
          icon: <PendingIcon fontSize="small" />,
        };
      case 'EXPIRED':
        return {
          label: 'Invitation Expired',
          color: 'error' as const,
          icon: <ExpiredIcon fontSize="small" />,
        };
      case 'CANCELLED':
        return {
          label: 'Invitation Cancelled',
          color: 'default' as const,
          icon: <ExpiredIcon fontSize="small" />,
        };
      case 'ACCEPTED':
        return {
          label: 'Has Account',
          color: 'success' as const,
          icon: <CheckCircleIcon fontSize="small" />,
        };
      default:
        return null;
    }
  };

  const invitationStatusInfo = getInvitationStatusInfo();
  const isInvitationPending = sendInvitation.isPending || resendInvitation.isPending || cancelInvitation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <PageHeader
          title="Contact Information"
          subtitle="Manage your family's contact details"
          breadcrumbs={[
            { label: 'Dashboard', path: '..' },
            { label: 'Contact Information' },
          ]}
        />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={400} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <PageHeader
          title="Contact Information"
          subtitle="Manage your family's contact details"
          breadcrumbs={[
            { label: 'Dashboard', path: '..' },
            { label: 'Contact Information' },
          ]}
        />
        <Alert severity="error">
          Failed to load contact information. Please try again.
        </Alert>
      </Box>
    );
  }

  const isUpdating = updateProfile.isPending;

  return (
    <Box>
      <PageHeader
        title="Family Info"
        subtitle="Manage your family's contact details"
        breadcrumbs={[
          { label: 'Dashboard', path: '..' },
          { label: 'Family Info' },
        ]}
        action={
          !isEditing && !profile?.contactDetailsLocked ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          ) : null
        }
      />

      {profile?.contactDetailsLocked && (
        <Alert
          severity="info"
          icon={<LockIcon />}
          sx={{ mb: 3 }}
        >
          Contact details are locked. Please contact {user?.schoolName || 'your school'} admin to make changes.
        </Alert>
      )}

      {/* Family Name */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Family Name
          </Typography>
          <TextField
            label="Family Name"
            value={formData.familyName}
            disabled
            InputProps={{ readOnly: true }}
            fullWidth
            helperText="Your family name is managed by your school. Please contact an admin to change it."
          />
        </CardContent>
      </Card>

      {/* Primary Contact */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                bgcolor: 'primary.light',
                borderRadius: '50%',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <PersonIcon />
            </Box>
            <Box>
              <Typography variant="h6">Primary Contact</Typography>
              <Typography variant="body2" color="text.secondary">
                The main contact for all school communications
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.contact1.firstName}
                onChange={(e) => handleChange('contact1', 'firstName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.contact1.lastName}
                onChange={(e) => handleChange('contact1', 'lastName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.contact1.email}
                onChange={(e) => handleChange('contact1', 'email', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={formData.contact1.phone}
                onChange={(e) => handleChange('contact1', 'phone', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Relationship"
                value={formData.contact1.relationship}
                onChange={(e) => handleChange('contact1', 'relationship', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
                helperText="e.g., Mother, Father, Guardian"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Secondary Contact */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                bgcolor: 'info.light',
                borderRadius: '50%',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'info.main',
              }}
            >
              <PersonOutlineIcon />
            </Box>
            <Box>
              <Typography variant="h6">Secondary Contact</Typography>
              <Typography variant="body2" color="text.secondary">
                Optional secondary contact for backup communications
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.contact2.firstName}
                onChange={(e) => handleChange('contact2', 'firstName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.contact2.lastName}
                onChange={(e) => handleChange('contact2', 'lastName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.contact2.email}
                onChange={(e) => handleChange('contact2', 'email', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={formData.contact2.phone}
                onChange={(e) => handleChange('contact2', 'phone', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Relationship"
                value={formData.contact2.relationship}
                onChange={(e) => handleChange('contact2', 'relationship', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                helperText="e.g., Mother, Father, Guardian, Grandparent"
              />
            </Grid>
          </Grid>

          {/* Portal Access Section */}
          {formData.contact2.email && !isEditing && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                  Portal Access
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Status display */}
                  {isLoadingInvitation ? (
                    <CircularProgress size={20} />
                  ) : invitationStatusInfo ? (
                    <Chip
                      icon={invitationStatusInfo.icon}
                      label={invitationStatusInfo.label}
                      color={invitationStatusInfo.color}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No portal access
                    </Typography>
                  )}

                  {/* Action buttons */}
                  {!isLoadingInvitation && invitationStatus && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Send invitation button */}
                      {invitationStatus.canInvite && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={isInvitationPending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
                          onClick={handleSendInvitation}
                          disabled={isInvitationPending}
                        >
                          Invite to Portal
                        </Button>
                      )}

                      {/* Resend invitation button */}
                      {invitationStatus.canResend && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={isInvitationPending ? <CircularProgress size={16} /> : <RefreshIcon />}
                          onClick={handleResendInvitation}
                          disabled={isInvitationPending}
                        >
                          Resend
                        </Button>
                      )}

                      {/* Cancel invitation button */}
                      {invitationStatus.status === 'PENDING' && (
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => setCancelDialogOpen(true)}
                          disabled={isInvitationPending}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Expiry info for pending invitations */}
                {invitationStatus?.status === 'PENDING' && invitationStatus.expiresAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Invitation expires: {new Date(invitationStatus.expiresAt).toLocaleDateString()}
                  </Typography>
                )}

                {/* Help text */}
                {invitationStatus?.status !== 'ALREADY_REGISTERED' && invitationStatus?.status !== 'ACCEPTED' && invitationStatus?.canInvite && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Send an invitation to allow {formData.contact2.firstName || 'the secondary contact'} to create their own login and access the portal.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cancel Invitation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the invitation sent to {formData.contact2.email}?
            They will no longer be able to use the invitation link to create an account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isInvitationPending}>
            Keep Invitation
          </Button>
          <Button
            onClick={handleCancelInvitation}
            color="error"
            disabled={isInvitationPending}
            startIcon={isInvitationPending ? <CircularProgress size={16} /> : undefined}
          >
            Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Contact */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                bgcolor: 'error.light',
                borderRadius: '50%',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main',
              }}
            >
              <EmergencyIcon />
            </Box>
            <Box>
              <Typography variant="h6">Emergency Contact</Typography>
              <Typography variant="body2" color="text.secondary">
                Person to contact in case of an emergency (required)
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.emergencyContact.firstName}
                onChange={(e) => handleChange('emergencyContact', 'firstName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.emergencyContact.lastName}
                onChange={(e) => handleChange('emergencyContact', 'lastName', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={formData.emergencyContact.phone}
                onChange={(e) => handleChange('emergencyContact', 'phone', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Relationship"
                value={formData.emergencyContact.relationship}
                onChange={(e) => handleChange('emergencyContact', 'relationship', e.target.value)}
                disabled={!isEditing || isUpdating}
                fullWidth
                required
                helperText="e.g., Grandparent, Aunt, Uncle, Family Friend"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Student Additional Information */}
      {children && children.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: 'warning.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'warning.main',
                }}
              >
                <InfoIcon />
              </Box>
              <Box>
                <Typography variant="h6">Student Additional Information</Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep each student's date of birth up to date, and share important details with the school (e.g. medical conditions, allergies, learning needs)
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {children.map((child, index) => {
                // A withdrawn child is read-only: the server refuses edits for them (#457, #455).
                const canEditChild = child.isActive !== false;
                const isEditingNotes = canEditChild && (notesEditing[child.id] ?? false);
                const draft = notesDraft[child.id] ?? (child.notes || '');
                const MAX_CHARS = 5000;

                const handleEditNotes = () => {
                  setNotesDraft((prev) => ({ ...prev, [child.id]: child.notes || '' }));
                  setNotesEditing((prev) => ({ ...prev, [child.id]: true }));
                };

                const handleCancelNotes = () => {
                  setNotesEditing((prev) => ({ ...prev, [child.id]: false }));
                };

                const storedDobKey = birthDateKey(child.birthDate);
                const dobNeedsCheck = isFirstOfJanuary(storedDobKey);
                const isEditingDob = canEditChild && (dobEditing[child.id] ?? false);
                const dobValue = dobDraft[child.id] ?? storedDobKey;

                const handleEditDob = () => {
                  // A 1 January date may be the placeholder: start empty so it is never saved by accident.
                  setDobDraft((prev) => ({ ...prev, [child.id]: dobNeedsCheck ? '' : storedDobKey }));
                  setDobEditing((prev) => ({ ...prev, [child.id]: true }));
                };

                const handleCancelDob = () => {
                  setDobEditing((prev) => ({ ...prev, [child.id]: false }));
                };

                const handleSaveDob = async () => {
                  if (!dobValue) return;
                  try {
                    await updateChildBirthDate.mutateAsync({ studentId: child.id, birthDate: dobValue });
                    setDobEditing((prev) => ({ ...prev, [child.id]: false }));
                    setSnackbar({
                      open: true,
                      message: isFirstOfJanuary(dobValue)
                        ? `Date of birth updated for ${child.firstName}. The school will check it, as 1 January is also used when a date is unknown.`
                        : `Date of birth updated for ${child.firstName}`,
                      severity: 'success',
                    });
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: apiErrorMessage(err, 'Failed to save the date of birth. Please try again.'),
                      severity: 'error',
                    });
                  }
                };

                const handleSaveNotes = async () => {
                  try {
                    await updateStudentNotes.mutateAsync({
                      studentId: child.id,
                      notes: draft.trim() || null,
                    });
                    setNotesEditing((prev) => ({ ...prev, [child.id]: false }));
                    setSnackbar({ open: true, message: `Additional info updated for ${child.firstName}`, severity: 'success' });
                  } catch (err) {
                    setSnackbar({ open: true, message: apiErrorMessage(err, 'Failed to save. Please try again.'), severity: 'error' });
                  }
                };

                return (
                  <Box key={child.id}>
                    {index > 0 && <Divider sx={{ mb: 3 }} />}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {child.firstName} {child.lastName}
                      </Typography>
                    </Box>

                    {/* Date of birth */}
                    <Box sx={{ mb: 2 }}>
                      {isEditingDob ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            label="Date of birth"
                            type="date"
                            size="small"
                            value={dobValue}
                            onChange={(e) => setDobDraft((prev) => ({ ...prev, [child.id]: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: '1900-01-01', max: todayKey, 'aria-label': `Date of birth for ${child.firstName}` }}
                            disabled={updateChildBirthDate.isPending}
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelDob}
                              disabled={updateChildBirthDate.isPending}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={updateChildBirthDate.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                              onClick={handleSaveDob}
                              disabled={updateChildBirthDate.isPending || !dobValue}
                            >
                              {updateChildBirthDate.isPending ? 'Saving…' : 'Save'}
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <CakeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color={storedDobKey ? 'text.primary' : 'text.secondary'}>
                            Date of birth:{' '}
                            {storedDobKey
                              ? formatUTCDate(child.birthDate, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU')
                              : 'not recorded yet'}
                          </Typography>
                          {dobNeedsCheck && canEditChild && (
                            <Typography variant="body2" color="warning.dark">
                              please check this is right
                            </Typography>
                          )}
                          {canEditChild ? (
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={handleEditDob}
                              aria-label={`${storedDobKey ? 'Change' : 'Add'} date of birth for ${child.firstName}`}
                            >
                              {storedDobKey ? 'Change' : 'Add'}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              (no longer enrolled: contact the school to change it)
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Additional information
                      </Typography>
                      {canEditChild && !isEditingNotes && (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={handleEditNotes}
                          aria-label={`Edit additional information for ${child.firstName}`}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>

                    {isEditingNotes ? (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={draft}
                          onChange={(e) => {
                            if (e.target.value.length <= MAX_CHARS) {
                              setNotesDraft((prev) => ({ ...prev, [child.id]: e.target.value }));
                            }
                          }}
                          placeholder="Enter any important information about this student — medical conditions, allergies, learning needs, etc."
                          helperText={`Please use Date of birth above for a birth date. ${draft.length} / ${MAX_CHARS} characters`}
                          disabled={updateStudentNotes.isPending}
                          sx={{ mb: 1.5 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelNotes}
                            disabled={updateStudentNotes.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={updateStudentNotes.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveNotes}
                            disabled={updateStudentNotes.isPending}
                          >
                            {updateStudentNotes.isPending ? 'Saving…' : 'Save'}
                          </Button>
                        </Stack>
                      </>
                    ) : (
                      <Typography variant="body2" color={child.notes ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
                        {child.notes || 'No additional information provided.'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons (when editing) */}
      {isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
